import { lazy, Suspense } from 'react'
import { Cursor } from '@/components/chrome/Cursor'
import { Grain } from '@/components/chrome/Grain'
import { Nav } from '@/components/chrome/Nav'
import { Preloader } from '@/components/chrome/Preloader'
import { isDebug, playgroundSection } from '@/lib/flags'
import { SmoothScroll } from '@/providers/SmoothScroll'
import { Footer } from '@/sections/Footer'
import { findSection, SECTIONS } from '@/sections/registry'
import styles from './App.module.css'

const MAIN_SECTIONS = SECTIONS.filter((section) => section.id !== 'footer')
const Leva = lazy(() => import('leva').then((module) => ({ default: module.Leva })))

/** Debug controls are not part of a visitor's startup download. */
function DebugTools() {
  if (!isDebug) return null
  return (
    <Suspense fallback={null}>
      <Leva collapsed />
    </Suspense>
  )
}

/**
 * The page. With `?section=<id>` it renders a single section instead (playground mode).
 *
 * `main.tsx` keeps React StrictMode on, so every effect runs, cleans up and runs again in
 * dev. Effects must clean up after themselves; `useGSAP` already does this for GSAP.
 */
export function App() {
  if (playgroundSection !== null) return <Playground id={playgroundSection} />

  return (
    <SmoothScroll>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <DebugTools />
      <Preloader />
      <Nav />
      <main id="main" tabIndex={-1}>
        {MAIN_SECTIONS.map(({ id, Component }) => (
          <Component key={id} />
        ))}
      </main>
      <Footer />
      <Cursor />
      <Grain />
    </SmoothScroll>
  )
}

/**
 * Playground: one section followed by a 100vh spacer (so scroll-driven work can run),
 * with no preloader or chrome, and the phase starting at `'ready'`.
 * A missing or unknown id shows a small index linking to every section.
 */
function Playground({ id }: { id: string }) {
  const entry = findSection(id)

  if (!entry) {
    return (
      <main className={styles.devIndex}>
        <h1 className={styles.devTitle}>Sections</h1>
        {id ? <p className={styles.devNote}>No section called “{id}”.</p> : null}
        <ul className={styles.devList} role="list">
          {SECTIONS.map((section) => (
            <li key={section.id}>
              <a href={`?section=${section.id}`}>
                <code>{section.id}</code> {section.label}
              </a>
            </li>
          ))}
          <li>
            <a href="/">
              <code>/</code> Full page
            </a>
          </li>
        </ul>
      </main>
    )
  }

  const { Component } = entry
  return (
    <SmoothScroll>
      <DebugTools />
      <main id="main" tabIndex={-1}>
        <Component />
        <div className={styles.spacer} aria-hidden="true" />
      </main>
    </SmoothScroll>
  )
}
